/** Longest merge chart, so a typo cannot build a huge pattern. */
export const MAX_MERGE_ROWS = 4000;

export const MERGE_CURVES = [
  { id: "linear", label: "Linear" },
  { id: "smoothstep", label: "Smoothstep" },
  { id: "ease", label: "Easing" },
];

/** Linear is the default progressive ramp. */
const DEFAULT_CURVE = "linear";
const DEFAULT_EASE_POWER = 2;

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function progress(index, count) {
  if (count <= 1) return 0;
  return index / (count - 1);
}

/**
 * Map 0..1 through the selected blend curve.
 */
export function applyMergeCurve(t, curve = DEFAULT_CURVE, easePower = DEFAULT_EASE_POWER) {
  const x = clamp(Number(t) || 0, 0, 1);
  const id = String(curve || DEFAULT_CURVE);
  if (id === "smoothstep") return x * x * (3 - 2 * x);
  if (id === "ease") {
    const p = clamp(Number(easePower) || DEFAULT_EASE_POWER, 1, 8);
    if (x < 0.5) return Math.pow(2 * x, p) / 2;
    return 1 - Math.pow(2 * (1 - x), p) / 2;
  }
  return x;
}

/**
 * Flat pattern with visual top at the end of the array (stringing start),
 * matching dissolve chart storage.
 */
function rowsToPattern(rowsTopToBottom, fallback) {
  const r = rowsTopToBottom.length;
  const c = rowsTopToBottom[0]?.length || 1;
  const pattern = Array.from({ length: c * r }, () => fallback);
  for (let fromTop = 0; fromTop < r; fromTop++) {
    const start = (r - fromTop - 1) * c;
    const row = rowsTopToBottom[fromTop];
    for (let col = 0; col < c; col++) pattern[start + col] = row[col];
  }
  return pattern;
}

/**
 * k distinct indices evenly spaced around a circle of n positions.
 * `phase` rotates where the error-diffusion run starts.
 */
export function balancedIndices(n, k, phase = 0) {
  const size = Math.max(0, Math.floor(Number(n)) || 0);
  const count = clamp(Math.floor(Number(k)) || 0, 0, size);
  if (size < 1 || count < 1) return [];
  if (count >= size) return Array.from({ length: size }, (_, i) => i);

  const start = ((Math.floor(Number(phase)) || 0) % size + size) % size;
  const out = [];
  let error = 0;
  for (let step = 0; step < size && out.length < count; step++) {
    const i = (start + step) % size;
    error += count;
    if (error * 2 >= size) {
      out.push(i);
      error -= size;
    }
  }
  return out.sort((a, b) => a - b);
}

/**
 * Boolean mask of color-B seats (length = cols).
 */
export function maskFromIndices(indices, cols) {
  const c = Math.max(1, Math.floor(Number(cols)) || 1);
  const mask = Array.from({ length: c }, () => false);
  for (const index of indices) {
    if (index >= 0 && index < c) mask[index] = true;
  }
  return mask;
}

function rotateMask(mask, shift) {
  const c = mask.length;
  if (c < 1) return [];
  const s = ((Math.floor(Number(shift)) || 0) % c + c) % c;
  if (s === 0) return mask.slice();
  const out = Array.from({ length: c }, () => false);
  for (let i = 0; i < c; i++) out[(i + s) % c] = mask[i];
  return out;
}

function masksEqual(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function isRotationOf(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  for (let s = 0; s < a.length; s++) {
    if (masksEqual(a, rotateMask(b, s))) return true;
  }
  return false;
}

function longestTrueRun(mask) {
  const c = mask.length;
  if (c < 1) return 0;
  let best = 0;
  let run = 0;
  // Walk twice to catch wraps around the tube.
  for (let i = 0; i < c * 2; i++) {
    if (mask[i % c]) {
      run += 1;
      best = Math.max(best, run);
    } else {
      run = 0;
    }
  }
  return Math.min(best, c);
}

/**
 * Flat-grid columns that are neighbors on the offset tubular chart.
 * Alternate rounds are half-step staggered (same idea as PatternOffset).
 */
export function tubeNeighborCols(row, col, cols) {
  const c = Math.max(1, Math.floor(Number(cols)) || 1);
  const neighbors = [((col - 1) % c + c) % c, (col + 1) % c];
  if (row <= 0) return neighbors;
  // Odd rounds sit in the staggered offset lane relative to the round above.
  if (row % 2 === 1) {
    neighbors.push(col, ((col - 1) % c + c) % c);
  } else {
    neighbors.push(col, (col + 1) % c);
  }
  return [...new Set(neighbors)];
}

/**
 * Lower is better. Penalize vertical/helix B stacks, repeated arrangements,
 * and fat clusters; lightly reward breaking perfect alternation.
 */
export function scoreMergeMask(mask, prevMask, rowIndex, cols) {
  const c = mask.length;
  let score = 0;

  const run = longestTrueRun(mask);
  if (run > 1) score += (run - 1) * 4;

  let bCount = 0;
  for (let i = 0; i < c; i++) if (mask[i]) bCount += 1;
  if (bCount > 0 && bCount < c) {
    if (bCount * 2 === c && run === 1) {
      // Perfect ABAB — fine within a row; costly if locked to the previous row.
      score += 1;
    }
  }

  if (prevMask && prevMask.length === c) {
    if (masksEqual(mask, prevMask)) score += 40;
    else if (isRotationOf(mask, prevMask)) score += 18;

    let vertical = 0;
    let helix = 0;
    for (let col = 0; col < c; col++) {
      if (mask[col] && prevMask[col]) vertical += 1;
      if (!mask[col]) continue;
      for (const n of tubeNeighborCols(rowIndex, col, cols)) {
        if (prevMask[n]) helix += 1;
      }
    }
    score += vertical * 5;
    score += helix * 2;

    // Checkerboard lock: every column differs from the one above.
    let flips = 0;
    for (let col = 0; col < c; col++) {
      if (mask[col] !== prevMask[col]) flips += 1;
    }
    if (flips === c && bCount > 0 && bCount < c) score += 12;
  }

  // Deterministic tie noise from row + mask fingerprint (tiny).
  let fingerprint = rowIndex * 17;
  for (let i = 0; i < c; i++) if (mask[i]) fingerprint += (i + 1) * 3;
  score += (fingerprint % 7) * 0.01;

  return score;
}

/**
 * Candidate B-masks for a round: balanced spacing at each start phase,
 * plus each rotation of those bases.
 */
export function candidateMasks(cols, bCount) {
  const c = clamp(Math.floor(Number(cols)) || 1, 1, MAX_MERGE_ROWS);
  const k = clamp(Math.floor(Number(bCount)) || 0, 0, c);
  if (k <= 0) return [Array.from({ length: c }, () => false)];
  if (k >= c) return [Array.from({ length: c }, () => true)];

  const seen = new Set();
  const out = [];
  const push = (mask) => {
    const key = mask.map((bit) => (bit ? "1" : "0")).join("");
    if (seen.has(key)) return;
    seen.add(key);
    out.push(mask);
  };

  for (let phase = 0; phase < c; phase++) {
    const base = maskFromIndices(balancedIndices(c, k, phase), c);
    for (let rot = 0; rot < c; rot++) push(rotateMask(base, rot));
  }
  return out;
}

/**
 * Pick the lowest-scoring mask; ties keep the first (stable) candidate.
 */
export function chooseMergeMask(cols, bCount, prevMask, rowIndex) {
  const candidates = candidateMasks(cols, bCount);
  let best = candidates[0];
  let bestScore = Infinity;
  for (const mask of candidates) {
    const score = scoreMergeMask(mask, prevMask, rowIndex, cols);
    if (score < bestScore) {
      bestScore = score;
      best = mask;
    }
  }
  return best;
}

/**
 * Per-round count of color-2 beads. Uses the full transition length: never
 * solid B before the last row when there is more than one row.
 */
export function mergeBCounts(rows, cols, curve, easePower) {
  const r = clamp(Math.floor(Number(rows)) || 0, 0, MAX_MERGE_ROWS);
  const c = clamp(Math.floor(Number(cols)) || 1, 1, MAX_MERGE_ROWS);
  if (r < 1) return [];
  if (r === 1) return [0];

  const raw = [];
  for (let i = 0; i < r; i++) {
    const u = applyMergeCurve(progress(i, r), curve, easePower);
    raw.push(clamp(Math.round(u * c), 0, c));
  }
  raw[0] = 0;
  raw[r - 1] = c;
  for (let i = 0; i < r - 1; i++) {
    raw[i] = Math.min(raw[i], c - 1);
  }

  const counts = [];
  let prev = 0;
  for (let i = 0; i < r; i++) {
    let next = Math.max(raw[i], prev);
    if (i < r - 1) next = Math.min(next, c - 1);
    counts.push(next);
    prev = next;
  }
  counts[r - 1] = c;
  return counts;
}

/**
 * One chart row (left → right) from a boolean B-mask.
 */
export function rowFromMask(color1, color2, mask) {
  return mask.map((isB) => (isB ? color2 : color1));
}

/**
 * One chart row with balanced B placement (no adjacency search).
 * Kept for tests and simpler call sites.
 */
export function placeMergeRound(color1, color2, cols, bCount, offset = 0) {
  const c = clamp(Math.floor(Number(cols)) || 1, 1, MAX_MERGE_ROWS);
  const k = clamp(Math.floor(Number(bCount)) || 0, 0, c);
  const mask = rotateMask(maskFromIndices(balancedIndices(c, k, 0), c), offset);
  return rowFromMask(color1, color2, mask);
}

/**
 * Fill the whole chart with a two-color tubular transition from color1
 * (visual top / stringing start) to color2 (visual bottom).
 */
export function mergeColorsPattern({
  color1,
  color2,
  cols,
  rows,
  curve = DEFAULT_CURVE,
  easePower = DEFAULT_EASE_POWER,
}) {
  const c = clamp(Math.floor(Number(cols)) || 1, 1, MAX_MERGE_ROWS);
  const r = clamp(Math.floor(Number(rows)) || 0, 0, MAX_MERGE_ROWS);
  const start = color1 == null || color1 === "" ? "#000000" : color1;
  const end = color2 == null || color2 === "" ? "#ffffff" : color2;

  if (r < 1) {
    return { pattern: [], cols: c, rows: 0, bCounts: [] };
  }

  const counts = mergeBCounts(r, c, curve, easePower);
  const topToBottom = [];
  let prevMask = null;
  for (let i = 0; i < r; i++) {
    const mask = chooseMergeMask(c, counts[i], prevMask, i);
    topToBottom.push(rowFromMask(start, end, mask));
    prevMask = mask;
  }

  return {
    pattern: rowsToPattern(topToBottom, start),
    cols: c,
    rows: r,
    bCounts: counts,
  };
}
